// React imports
import { FC, useEffect, useState, useCallback } from "react";

// External libraries
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Form,
  Input,
  notification,
  Row,
  Tabs,
  Button,
  Space,
  InputNumber,
  Card,
  Typography,
  Divider,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";

// Internal components
import { ActivityIndicator } from "../../ActivityIndicator";
import { TranslationForm } from "./components/TranslationForm";

// Hooks and API
import { useToolsIA } from "../../../hooks/useToolsIA";
import UseGetToolsIAById from "../../../api/useGetToolsIAById";
import UseCreateToolsIA from "../../../api/useCreateToolsIA";
import UseUpdateToolsIA from "../../../api/useUpdateToolsIA";

// Types
import { ToolsIAInput } from "../../../__generated__/gql-types/globalTypes";
import { getToolsIA_getToolsIA } from "../../../__generated__/gql-types/getToolsIA";

// Utils and styles
import {
  errorNotification,
  successNotification,
  updatedNotification,
} from "./utils";

import { getValidationMessages } from "./utils/validation/validationMessages";
import {
  FORM_LABELS,
  PLACEHOLDERS,
  BUTTON_LABELS,
  PAGE_TITLES,
  SUPPORTED_LANGUAGES,
  NOTIFICATION_MESSAGES,
  NOTIFICATION_CONFIG,
} from "./constants";

const { TabPane } = Tabs;

const NewToolsIAForm: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toolsIA, loading: isLoadingToolsIA } = useToolsIA();
  const toolsIA_id = location?.state as string;
  const getToolsIAById = UseGetToolsIAById();
  const createToolsIA = UseCreateToolsIA();
  const updateToolsIA = UseUpdateToolsIA();
  const [loading, setLoading] = useState<boolean>(true);
  const [form] = Form.useForm();
  const [currentLanguage, setCurrentLanguage] = useState<string>("es");

  const handleLanguageChange = (newLanguage: string) => {
    setCurrentLanguage(newLanguage);
  };

  const fetchInfo = useCallback(async () => {
    try {
      if (toolsIA_id) {
        const data = await getToolsIAById({ getToolsIaByIdId: toolsIA_id });

        if (data?.id) {
          const initialValues = {
            translations: {
              es: {
                title: data.title || "",
                description: data.description || "",
                prompt: data.prompt || "",
                cover: data.cover || "",
                content: data.forms || [],
              },
              en: {
                title: data.title_en || "",
                description: data.description_en || "",
                prompt: data.prompt_en || "",
                cover: data.cover_en || "",
                content: data.forms_en || [],
              },
            },
          };
          form.setFieldsValue(initialValues);
        } else {
          // Si no hay data, asegurar que existe la estructura mínima
          const defaultValues = {
            translations: {
              es: {
                title: "",
                description: "",
                prompt: "",
                cover: "",
                content: [],
              },
              en: {
                title: "",
                description: "",
                prompt: "",
                cover: "",
                content: [],
              },
            },
          };
          form.setFieldsValue(defaultValues);
        }
      } else {
        // Si no hay toolsIA_id, es modo creación - asegurar estructura
        const defaultValues = {
          translations: {
            es: {
              title: "",
              description: "",
              prompt: "",
              cover: "",
              content: [],
            },
            en: {
              title: "",
              description: "",
              prompt: "",
              cover: "",
              content: [],
            },
          },
        };
        form.setFieldsValue(defaultValues);
      }
    } catch (error) {
      notification.error({
        message: NOTIFICATION_MESSAGES.error.loading.title,
        description: NOTIFICATION_MESSAGES.error.loading.description,
        placement: NOTIFICATION_CONFIG.placement,
        duration: NOTIFICATION_CONFIG.duration,
      });
    } finally {
      setLoading(false);
    }
  }, [toolsIA_id, getToolsIAById, form]);

  useEffect(() => {
    fetchInfo();
  }, [fetchInfo]);

  const handleSubmit = async (values: any) => {
    try {
      if (loading || isLoadingToolsIA) return;
      
      // Validación defensiva: asegurar que existe la estructura de translations
      if (!values.translations) {
        console.error('Error: values.translations is undefined');
        errorNotification();
        return;
      }
      
      // Inicializar datos por defecto si están vacíos
      const esData = values.translations.es || {};
      const enData = values.translations.en || {};

      const toolsIAInput: ToolsIAInput = {
        title: esData.title || "",
        title_en: enData.title || "",
        description: esData.description || "",
        description_en: enData.description || "",
        prompt: esData.prompt || "",
        prompt_en: enData.prompt || "",
        cover: esData.cover || "",
        cover_en: enData.cover || "",
        forms: (esData.content || []).map((item: any) => ({
          example_text: item.example_text || "",
          label: item.label || "",
          number_of_lines: Number(item.number_of_lines) || 1,
          section: Number(item.section) || 0,
        })),
        forms_en: (enData.content || []).map((item: any) => ({
          example_text: item.example_text || "",
          label: item.label || "",
          number_of_lines: Number(item.number_of_lines) || 1,
          section: Number(item.section) || 0,
        })),
      };
      
      if (toolsIA_id && toolsIA_id.length > 0) {
        const updateResponse = await updateToolsIA({
          toolsIaInput: toolsIAInput,
          updateToolsIaId: toolsIA_id,
        });
        updateResponse && updatedNotification();
        const index = toolsIA.findIndex(
          (toolIA) => toolIA.id === updateResponse.data.updateToolsIA.id
        );
        const toolsIAUpdated = [...toolsIA];
        toolsIAUpdated[index] = updateResponse.data
          .updateToolsIA as unknown as getToolsIA_getToolsIA;
        dispatch({ type: "data/setToolsIA", payload: toolsIAUpdated });
        navigate(-1);
      } else {
        const createResponse = await createToolsIA({
          toolsIaInput: toolsIAInput,
        });
        
        if (createResponse?.data?.createToolsIA) {
          successNotification();
          
          // Agregar el nuevo item al store
          const newToolsIA = createResponse.data.createToolsIA as unknown as getToolsIA_getToolsIA;
          const updatedToolsIA = [newToolsIA, ...toolsIA].sort(
            (a, b) => Number(b?.id) - Number(a?.id)
          );
          dispatch({ type: "data/setToolsIA", payload: updatedToolsIA });
        }
        
        navigate(-1);
      }
    } catch (error) {
      console.error("Error:", error);
      setLoading(false);
      errorNotification();
    }
  };

  if (loading || isLoadingToolsIA) {
    return <ActivityIndicator />;
  }
  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        background: "#f5f6fa",
        display: "flex",
        flexDirection: "column",
        alignItems: "left",
        padding: 0,
        margin: 0,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 1200,
          padding: 0,
        }}
      >
        <Card
          style={{
            borderRadius: 18,
            boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
            padding: 36,
            margin: 10,
            background: "#fff",
          }}
          bodyStyle={{ padding: 0 }}
        >
          <Typography.Title
            level={2}
            style={{ marginBottom: 32, textAlign: "center" }}
          >
            {toolsIA_id ? PAGE_TITLES.edit : PAGE_TITLES.create}
          </Typography.Title>
          <Form
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
            initialValues={{
              translations: {
                es: {
                  title: "",
                  description: "",
                  prompt: "",
                  cover: "",
                  content: [],
                },
                en: {
                  title: "",
                  description: "",
                  prompt: "",
                  cover: "",
                  content: [],
                },
              },
            }}
          >
            <Tabs
              activeKey={currentLanguage}
              onChange={handleLanguageChange}
              type="card"
              centered={false}
              style={{ marginBottom: 32 }}
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <TabPane tab={lang.toUpperCase()} key={lang}>
                  <div
                    style={{
                      display: currentLanguage === lang ? "block" : "none",
                    }}
                  >
                    <TranslationForm
                      language={lang}
                      form={form}
                      name={["translations", lang]}
                    />
                    <Divider style={{ margin: "32px 0" }}>
                      {FORM_LABELS.contentElements}
                    </Divider>
                    <Form.List
                      key={`content-list-${lang}`}
                      name={["translations", lang, "content"]}
                    >
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map((field, index) => (
                            <div
                              key={`${lang}-${field.key}`}
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                margin: "20px 0",
                                padding: 20,
                                border: "1px solid #d9d9d9",
                                borderRadius: "8px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: 16,
                                }}
                              >
                                <label style={{ fontWeight: 500 }}>
                                  {FORM_LABELS.contentItem} #{index + 1}
                                </label>
                                <Space>
                                  {fields.length > 0 && (
                                    <Button
                                      type="text"
                                      danger
                                      icon={<DeleteOutlined />}
                                      onClick={() => remove(field.name)}
                                      aria-label={`Eliminar contenido ${
                                        index + 1
                                      }`}
                                    />
                                  )}
                                </Space>
                              </div>

                              <Form.Item
                                key={`${lang}-section-${field.key}`}
                                name={[field.name, "section"]}
                                fieldKey={field.fieldKey}
                                hidden
                                initialValue={index}
                              >
                                <Input />
                              </Form.Item>

                              <Form.Item
                                key={`${lang}-label-${field.key}`}
                                label={`Título (Elemento ${index + 1})`}
                                name={[field.name, "label"]}
                                fieldKey={field.fieldKey}
                                rules={[
                                  {
                                    required: true,
                                    message:
                                      getValidationMessages(lang).titleRequired,
                                  },
                                ]}
                              >
                                <Input
                                  placeholder={
                                    PLACEHOLDERS[
                                      lang as keyof typeof PLACEHOLDERS
                                    ]?.contentTitle
                                  }
                                />
                              </Form.Item>

                              <Form.Item
                                key={`${lang}-example-${field.key}`}
                                label={FORM_LABELS.exampleText}
                                name={[field.name, "example_text"]}
                                fieldKey={field.fieldKey}
                                rules={[
                                  {
                                    required: true,
                                    message:
                                      getValidationMessages(lang)
                                        .exampleTextRequired,
                                  },
                                ]}
                              >
                                <Input.TextArea
                                  placeholder={
                                    PLACEHOLDERS[
                                      lang as keyof typeof PLACEHOLDERS
                                    ]?.exampleText
                                  }
                                  autoSize={{ minRows: 2 }}
                                />
                              </Form.Item>

                              <Form.Item
                                key={`${lang}-lines-${field.key}`}
                                label={FORM_LABELS.numberOfLines}
                                name={[field.name, "number_of_lines"]}
                                fieldKey={field.fieldKey}
                                rules={[
                                  {
                                    required: true,
                                    message:
                                      getValidationMessages(lang)
                                        .numberOfLinesRequired,
                                  },
                                  {
                                    type: "number",
                                    min: 1,
                                    message:
                                      getValidationMessages(lang).minimumLines,
                                  },
                                ]}
                              >
                                <InputNumber
                                  placeholder={
                                    PLACEHOLDERS[
                                      lang as keyof typeof PLACEHOLDERS
                                    ]?.lines
                                  }
                                  style={{ width: "100%" }}
                                  min={1}
                                />
                              </Form.Item>
                            </div>
                          ))}
                          <Form.Item style={{ marginTop: 16 }}>
                            <Button
                              type="dashed"
                              onClick={() =>
                                add({
                                  label: "",
                                  example_text: "",
                                  number_of_lines: 1,
                                  section: fields.length,
                                })
                              }
                              block
                              icon={<PlusOutlined />}
                            >
                              {BUTTON_LABELS.addContent}
                            </Button>
                          </Form.Item>
                        </>
                      )}
                    </Form.List>
                  </div>
                </TabPane>
              ))}
            </Tabs>
            <Row justify="end">
              <Space>
                <Button onClick={() => navigate(-1)} size="large">
                  {BUTTON_LABELS.cancel}
                </Button>
                <Button type="primary" htmlType="submit" size="large">
                  {BUTTON_LABELS.save}
                </Button>
              </Space>
            </Row>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default NewToolsIAForm;
